import { useEffect, useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { Footer } from "../../../components/landing"
import { EffectProvider } from "../../../effects"
import { useMediaQuery } from "../../../hooks/useMediaQuery"
import { useTheme } from "../../../theme/ThemeProvider"
import { useBrand } from "../../../theme/useBrand"
import { HowItWorksBackground } from "./HowItWorksBackground"
import ChapteredDemo from "../../../components/demo/ChapteredDemo"
import { PRICING_PLANS } from "../../../data/pricing"
import styles from "./HowItWorks.module.css"

type SolutionCard = {
  icon: string
  title: string
  description: string
}

const SOLUTION_CARDS: SolutionCard[] = [
  {
    icon: "◈",
    title: "Invoicing",
    description: '"Invoice Gary $300". Invoice created, sent, and tracked in seconds.',
  },
  {
    icon: "✦",
    title: "Collecting",
    description: "Friendly reminders go out on schedule. Follow-up is automatic.",
  },
  {
    icon: "◎",
    title: "Learning",
    description: "Your agent learns payment patterns and adjusts timing per customer.",
  },
]

function PricingComparisonTable() {
  return (
    <table className={styles.comparisonTable}>
      <thead>
        <tr>
          <th>Feature</th>
          <th>Basic</th>
          <th>Pro</th>
          <th>Premium</th>
          <th>Team</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>SMS invoicing</td>
          <td>Yes</td>
          <td>Yes</td>
          <td>Yes</td>
          <td>Yes</td>
        </tr>
        <tr>
          <td>Automated reminders</td>
          <td>Yes</td>
          <td>Yes</td>
          <td>Yes</td>
          <td>Yes</td>
        </tr>
        <tr>
          <td>Custom workflows</td>
          <td>No</td>
          <td>No</td>
          <td>Yes</td>
          <td>Yes</td>
        </tr>
        <tr>
          <td>API access</td>
          <td>No</td>
          <td>No</td>
          <td>Yes</td>
          <td>Yes</td>
        </tr>
        <tr>
          <td>Team members</td>
          <td>1</td>
          <td>1</td>
          <td>1</td>
          <td>5</td>
        </tr>
        <tr>
          <td>Overdue escalation sequences</td>
          <td>No</td>
          <td>No</td>
          <td>No</td>
          <td>Yes</td>
        </tr>
      </tbody>
    </table>
  )
}


export function HowItWorksPage() {
  const navigate = useNavigate()
  const isMobile = useMediaQuery("(max-width: 767px)")
  const { theme } = useTheme()
  const personaName = theme === "fairy" ? "Fairy" : "Demon"
  const brand = useBrand()

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  const [activeSolutionIndex, setActiveSolutionIndex] = useState(1)

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" })
  }, [])

  useEffect(() => {
    if (!isMobile) {
      setMobileMenuOpen(false)
    }
  }, [isMobile])

  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? "hidden" : ""
    return () => {
      document.body.style.overflow = ""
    }
  }, [mobileMenuOpen])

  useEffect(() => {
    const cycle = window.setInterval(() => {
      setActiveSolutionIndex((prev) => (prev + 1) % SOLUTION_CARDS.length)
    }, 2800)
    return () => window.clearInterval(cycle)
  }, [])

  const scrollToSection = (id: string) => {
    const section = document.getElementById(id)
    if (section) {
      section.scrollIntoView({ behavior: "smooth", block: "start" })
    }
  }


  const handleMobilePrimaryAction = () => {
    navigate("/onboard/contact")
  }

  const desktopNavLinks = [
    { label: "Chaos", id: "problem" },
    { label: "Solution", id: "solution" },
    { label: "Pricing", id: "pricing" },
  ]

  const mobileHeroMeta = "14-day free trial • No credit card • Setup takes 2 minutes"

  return (
    <EffectProvider>
      <main className={styles.page}>
        <HowItWorksBackground />

        <header className={styles.header}>
          <Link to="/" className={styles.brand} aria-label={`${brand.name} home`}>
            <img src={brand.sprite} className={`${styles.brandSprite} pixel`} alt="" />
            <span className={styles.brandText}>{brand.wordmark}</span>
          </Link>

          {isMobile ? null : (
            <nav className={styles.desktopNav} aria-label="How it works navigation">
              {desktopNavLinks.map((link) => (
                <button key={link.id} className={styles.navLink} onClick={() => scrollToSection(link.id)}>
                  {link.label}
                </button>
              ))}
              <Link to="/login" className={styles.navLink}>
                Login
              </Link>
            </nav>
          )}

          <button
            type="button"
            className={`${styles.mobileMenuButton} ${mobileMenuOpen ? styles.mobileMenuButtonOpen : ""}`}
            onClick={() => setMobileMenuOpen((prev) => !prev)}
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileMenuOpen}
            aria-controls="how-it-works-mobile-drawer"
          >
            <span className={styles.mobileMenuGlyph} />
            <span className={styles.mobileMenuGlyph} />
            <span className={styles.mobileMenuGlyph} />
          </button>
        </header>

        <button
          type="button"
          className={`${styles.mobileDrawerBackdrop} ${mobileMenuOpen ? styles.mobileDrawerBackdropOpen : ""}`}
          onClick={() => setMobileMenuOpen(false)}
          aria-label="Close navigation menu"
          aria-hidden={!mobileMenuOpen}
        />

        <aside id="how-it-works-mobile-drawer" className={`${styles.mobileDrawer} ${mobileMenuOpen ? styles.mobileDrawerOpen : ""}`}>
          <div className={styles.mobileDrawerTitle}>
            <img src={brand.sprite} width={14} height={14} className="pixel" alt="" /> Navigate
          </div>
          <div className={styles.mobileDrawerLinks}>
            <Link className={styles.mobileDrawerLink} to="/" onClick={() => setMobileMenuOpen(false)}>
              Home
            </Link>
            <Link className={styles.mobileDrawerLink} to="/pricing" onClick={() => setMobileMenuOpen(false)}>
              Pricing
            </Link>
            <Link className={styles.mobileDrawerLink} to="/login" onClick={() => setMobileMenuOpen(false)}>
              Login
            </Link>
            <Link
              className={`${styles.mobileDrawerLink} ${styles.mobileDrawerCta}`}
              to="/onboard/contact"
              onClick={() => setMobileMenuOpen(false)}
            >
              Summon your {personaName.toLowerCase()}
            </Link>
          </div>
        </aside>

        {isMobile ? (
          <div className={styles.mobileTheatre}>
            <section style={{ padding: "108px 16px 8px", display: "grid", gap: "10px" }}>
              <h1
                className="mono"
                style={{
                  margin: 0,
                  fontSize: "clamp(26px, 8vw, 34px)",
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  lineHeight: 1.2,
                }}
              >
                Text your {personaName}.
                <br />
                Get paid faster.
              </h1>
              <p className="mono" style={{ margin: 0, fontSize: "13px", lineHeight: 1.6, color: "var(--muted)" }}>
                Your AI invoicing agent lives in your messages. Text it a job — it invoices, confirms
                anything risky with you first, chases overdue payments, and logs everything. Watch a
                real conversation:
              </p>
            </section>

            <section style={{ padding: "8px 16px 140px" }}>
              <ChapteredDemo embedded />
            </section>
          </div>
        ) : (
          <div className={styles.content}>
            <section id="hero" className={styles.hero}>
              <div className={styles.heroText}>
                <p className={styles.heroLabel}>AI-powered back office</p>
                <h1 className={styles.heroTitle}>
                  Text your {personaName.toLowerCase()}.
                  <span className={styles.heroTitleAccent}>Get paid faster.</span>
                </h1>
                <p className={styles.heroSubtitle}>
                  Your AI invoicing agent lives in your messages. Text it a job — it invoices,
                  confirms anything risky with you first, chases overdue payments, and logs everything.
                </p>
                <div className={styles.heroButtons}>
                  <button type="button" className={styles.primaryButton} onClick={() => navigate("/onboard/contact")}>
                    Summon your {personaName.toLowerCase()}
                  </button>
                  <button
                    type="button"
                    className={styles.secondaryButton}
                    onClick={() => scrollToSection("live-demo")}
                  >
                    Watch the demo
                  </button>
                </div>
                <p className={styles.helperText}>See how it works. No credit card required.</p>
              </div>
            </section>

            <section id="live-demo" aria-label="Product demo" style={{ margin: "0 auto", width: "min(1100px, 100%)", padding: "24px 0 8px" }}>
              <ChapteredDemo embedded />
            </section>

            <section id="problem" className={styles.problem}>
              <p className={styles.sectionLabel}>Chaos</p>
              <h2 className={styles.sectionTitle}>Your inbox is doing too much work.</h2>
              <p className={styles.sectionCopy}>Jobs finished, invoices delayed, reminders forgotten. Chaos blocks cash flow.</p>

              <div className={styles.problemGrid}>
                <article className={styles.chaosCard}>
                  <h3 className={styles.chaosHeader}>Inbox from hell</h3>
                  <ul className={styles.chaosList}>
                    <li className={styles.chaosItem}>
                      <span className={styles.chaosDot}>!</span>
                      <span>&ldquo;Did you get my invoice yet?&rdquo;</span>
                    </li>
                    <li className={styles.chaosItem}>
                      <span className={styles.chaosDot}>!</span>
                      <span>&ldquo;Can you resend for $1,500 kitchen work?&rdquo;</span>
                    </li>
                    <li className={styles.chaosItem}>
                      <span className={styles.chaosDot}>!</span>
                      <span>&ldquo;Reminder: Sarah is now 14 days overdue.&rdquo;</span>
                    </li>
                  </ul>
                  <p className={styles.chaosMeta}>You should be doing the work, not chasing payments.</p>
                </article>

                <article className={styles.chaosCard}>
                  <h3 className={styles.chaosHeader}>What this costs</h3>
                  <ul className={styles.chaosList}>
                    <li className={styles.chaosItem}>
                      <span className={styles.chaosDot}>▲</span>
                      <span>Longer payment cycles</span>
                    </li>
                    <li className={styles.chaosItem}>
                      <span className={styles.chaosDot}>▲</span>
                      <span>Missed follow-ups</span>
                    </li>
                    <li className={styles.chaosItem}>
                      <span className={styles.chaosDot}>▲</span>
                      <span>Inconsistent customer communication</span>
                    </li>
                  </ul>
                  <p className={styles.chaosMeta}>SalesDemon gives you one text-first control center.</p>
                </article>
              </div>
            </section>

            <section id="solution" className={styles.solution}>
              <p className={styles.sectionLabel}>Solution</p>
              <h2 className={styles.sectionTitle}>How your demon handles the chaos.</h2>

              <div className={styles.solutionsGrid}>
                {SOLUTION_CARDS.map((card, index) => {
                  const isActive = index === activeSolutionIndex
                  return (
                    <article key={card.title} className={`${styles.solutionCard} ${isActive ? styles.solutionCardActive : ""}`}>
                      <h3 className={styles.solutionCardTitle}>
                        <span>{card.icon}</span> {card.title}
                      </h3>
                      <p className={styles.solutionCardBody}>{card.description}</p>
                    </article>
                  )
                })}
              </div>

              <div className={styles.beforeAfter}>
                <div className={styles.beforeColumn}>
                  <h3 className={styles.compareTitle}>Before (Hell)</h3>
                  <ul className={styles.compareList}>
                    <li>Chase Gary for payment</li>
                    <li>Remember to send invoices manually</li>
                    <li>Guess who needs a reminder</li>
                  </ul>
                </div>

                <div className={styles.afterColumn}>
                  <h3 className={styles.compareTitle}>After (Demon)</h3>
                  <ul className={styles.compareList}>
                    <li>Demon reminds customers automatically</li>
                    <li>&ldquo;Invoice Gary&rdquo; done in seconds</li>
                    <li>Patterns learned and acted on</li>
                  </ul>
                </div>
              </div>
            </section>

            <section id="pricing" className={styles.pricing}>
              <p className={styles.sectionLabel}>Pricing</p>
              <h2 className={styles.sectionTitle}>Pick a plan and launch in minutes.</h2>

              <div className={styles.pricingGrid} role="list" aria-label="Plans">
                {PRICING_PLANS.map((plan) => (
                  <article
                    key={plan.name}
                    role="listitem"
                    className={`${styles.pricingCard} ${plan.featured ? styles.pricingFeatured : ""}`}
                  >
                    {plan.featured && <span className={styles.pricingBadge}>Most popular</span>}
                    <h3 className={styles.planName}>{plan.name}</h3>
                    <p className={styles.planPrice}>
                      ${plan.price}
                      <span className={styles.planPriceUnit}>/month</span>
                    </p>
                    <p className={styles.planSubtitle}>{plan.subtitle}</p>
                    <ul className={styles.planFeatureList}>
                      {plan.features.map((feature) => (
                        <li key={feature} className={styles.planFeature}>
                          ✓ {feature}
                        </li>
                      ))}
                    </ul>
                    <p className={styles.planNote}>{plan.note}</p>
                    <Link to="/onboard/intro" className={styles.planButton}>
                      {plan.cta}
                    </Link>
                  </article>
                ))}
              </div>

              <details className={styles.comparisonWrap}>
                <summary className={styles.comparisonToggle}>Full pricing compare</summary>
                <PricingComparisonTable />
              </details>
            </section>

            <section id="appendix" className={styles.appendix}>
              <h3 className={styles.appendixTitle}>For technical and skeptical users</h3>
              <p className={styles.appendixCopy}>Need internals? Review the execution trace below without interrupting the buying flow.</p>
              <details className={styles.techLog}>
                <summary>See the log</summary>
                <pre>{`[demo.process.log]
- inbound: sms.intent.invoice
- context: tenant+customer scope loaded
- action: invoice.created + delivery.sms
- reminders: scheduled day_7
- callback: payment.received
- audit: appended + replay safe`}</pre>
              </details>
            </section>
          </div>
        )}

        <div className={styles.stickyCtaBar}>
          {isMobile ? (
            <div className={styles.mobileStickyWrap}>
              <button type="button" className={styles.mobileStickyPrimary} onClick={handleMobilePrimaryAction}>
                Summon your {personaName.toLowerCase()}
              </button>
              <p className={styles.mobileStickyMeta}>{mobileHeroMeta}</p>
            </div>
          ) : (
            <div className={styles.stickyCtaInner}>
              <button type="button" className={styles.stickyPrimary} onClick={() => navigate("/onboard/contact")}>
                Summon your {personaName.toLowerCase()}
              </button>
              <button
                type="button"
                className={styles.stickySecondary}
                onClick={() => scrollToSection("live-demo")}
              >
                Watch the demo
              </button>
            </div>
          )}
        </div>

        <Footer />
      </main>
    </EffectProvider>
  )
}

export default HowItWorksPage
